import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObservable } from '@/shared/hooks/useObservable';
import { authService } from '@/features/auth/services/auth.service';
import { notificationService } from '@/features/notification/services/notification.service';
import { mapService } from '@/features/map/services/map.service';
import { MapContainer } from '@/features/map/components/MapContainer';
import { DrawingTools } from '@/features/map/components/DrawingTools';
import { ZoneOverlay } from '@/features/map/components/ZoneOverlay';
import { StepperPanel } from '@/features/notification/components/StepperPanel';
import { FlightsPanel } from '@/features/flights/components/FlightsPanel';
import { FlightsOverlay } from '@/features/flights/components/FlightsOverlay';
import { applyFlightToWizard } from '@/features/flights/utils/duplicate-flight';
import type { FlightNotification } from '@/features/flights/api/flights.api';
import { AddressAutocomplete } from '@/shared/components/AddressAutocomplete';
import { KmlLayersOverlay } from '@/features/map-layers/components/KmlLayersOverlay';
import { KmlLayerControl } from '@/features/map-layers/components/KmlLayerControl';

const PANEL_WIDTH = 340;

export function NotificationPage() {
  const user = useObservable(authService.user$, null);
  const formData = useObservable(notificationService.formData$, notificationService.formData$.value);
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(true);
  const [flightsPanelOpen, setFlightsPanelOpen] = useState(false);
  const [mapSearch, setMapSearch] = useState('');

  function handleLogout() {
    authService.logout();
    notificationService.reset();
    navigate('/login');
  }

  function handleDuplicateFlight(flight: FlightNotification) {
    if (!user) return;
    applyFlightToWizard(flight, user);
    setFlightsPanelOpen(false);
    setPanelOpen(true);
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-slate-900">
      {/* Navbar */}
      <nav className="h-14 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 flex items-center px-4 flex-shrink-0 z-50">
        <div className="flex items-center gap-2">
          <span className="text-xl">🛸</span>
          <span className="text-sm font-bold text-slate-100">Najava Leta</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {user && (
            <span className="text-xs text-slate-400">{user.name}</span>
          )}
          <button
            onClick={() => setFlightsPanelOpen(true)}
            className="text-xs text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 px-3 py-1.5 rounded-lg transition-colors"
          >
            Moje najave / My flights
          </button>
          <button
            onClick={handleLogout}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Odjava / Logout
          </button>
        </div>
      </nav>

      {flightsPanelOpen && (
        <FlightsPanel onClose={() => setFlightsPanelOpen(false)} onDuplicate={handleDuplicateFlight} />
      )}

      {/* Content row */}
      <div className="flex-1 relative overflow-hidden">
        {/* Map area — leaves space for open panel */}
        <div
          className="absolute inset-0 transition-all duration-200"
          style={{ right: panelOpen ? PANEL_WIDTH : 0 }}
        >
          <MapContainer sizeKey={panelOpen}>
            <DrawingTools category={formData.category} />
            <ZoneOverlay />
            <FlightsOverlay />
            <KmlLayersOverlay />
          </MapContainer>

          {/* Floating map search */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] w-80 shadow-xl">
            <AddressAutocomplete
              value={mapSearch}
              onChange={setMapSearch}
              placeholder="Iskanje lokacije / Search location..."
              onPlaceSelect={(lat, lng, address) => {
                setMapSearch(address);
                mapService.flyTo(lat, lng, 16);
                mapService.setTakeoffPoint({ lat, lng });
              }}
            />
          </div>

          <KmlLayerControl />
        </div>

        {/* Panel collapse toggle */}
        <button
          onClick={() => setPanelOpen(v => !v)}
          className="absolute top-1/2 z-[1000] -translate-y-1/2  w-5 h-10 bg-slate-800 border border-slate-700 border-r-0 rounded-l-md flex items-center justify-center text-slate-400 hover:text-slate-200 transition-all duration-200"
          style={{ right: panelOpen ? PANEL_WIDTH : 0 }}
          title={panelOpen ? 'Skrij panel / Hide panel' : 'Pokaži panel / Show panel'}
        >
          {panelOpen ? '›' : '‹'}
        </button>

        {/* Stepper panel */}
        {panelOpen && (
          <div
            className="absolute top-0 right-0 bottom-0"
            style={{ width: PANEL_WIDTH }}
          >
            <StepperPanel />
          </div>
        )}
      </div>
    </div>
  );
}
